import { makeStyles } from '@material-ui/styles'
import UsageWidget from 'core/components/widgets/UsageWidget'
import { METRICS_API_KEYS } from 'core/components/widgets/useTrendData'
import Theme from 'core/themes/model'
import { useEffect, useState } from 'react'
import { getHostUsageTotals, getHostAllocationTotals } from './helpers'
import React, { useMemo } from 'react'
import Text from 'core/elements/Text'

interface Props {
  hosts: any[]
  hypervisors?: any[]
  storagePools?: any[]
  perHostMetrics?: boolean
  showAllocation?: boolean
  showOnlyForSelectedHosts?: boolean
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'grid',
    gap: 8,
  },
  hostCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
    marginBottom: 12,
  },
  sectionHeader: {
    backgroundColor: theme.components.card.background,
    borderRadius: '4px 4px 2px 2px',
    border: `1px solid ${theme.components.card.border}`,
    padding: '16px 24px',
  },
}))

export default function HostsListPageHeader({
  hosts,
  hypervisors = [],
  storagePools = [],
  perHostMetrics = false,
  showAllocation = false,
  showOnlyForSelectedHosts = false,
}: Props) {
  const classes = useStyles()
  const totals = getHostUsageTotals(hosts)

  const allocation = useMemo(() => {
    if (!showAllocation) {
      return {
        compute: {
          current: 0,
          max: 0,
          percent: 0,
        },
        memory: {
          current: 0,
          max: 0,
          percent: 0,
        },
        storage: {
          current: 0,
          max: 0,
          percent: 0,
        },
      }
    }
    return getHostAllocationTotals({ hosts, hypervisors, storagePools })
  }, [hosts, hypervisors, storagePools, showAllocation])

  const hostIds = useMemo(() => hosts.map((host) => host.id), [hosts])

  return (
    <div className={classes.container}>
      {showAllocation && (
        <>
          <div className={classes.sectionHeader}>
            <Text variant="subtitle2">Allocation</Text>
            <Text variant="body2">
              Shows the total virtualized resources allocated across all the virtual machines
              provisioned on {perHostMetrics ? <b>{hosts?.[0]?.name}</b> : 'the hosts'}.
            </Text>
          </div>
          <div className={classes.hostCards}>
            <UsageWidget
              icon="microchip"
              title="CPU"
              stats={allocation?.compute}
              units="vCPUs"
              primary={`out of ${allocation?.compute?.max} vCPUs`}
              allocation
            />
            <UsageWidget
              icon="memory"
              title="Memory"
              stats={allocation?.memory}
              units="GiB"
              primary={`out of ${allocation?.memory?.max?.toFixed(1)} GiB`}
              allocation
            />
            <UsageWidget
              icon="sim-card"
              title="Storage"
              stats={allocation?.storage}
              units="GB"
              primary={`out of ${allocation?.storage?.max?.toFixed(1)} GB`}
              allocation
            />
          </div>
          <div className={classes.sectionHeader}>
            <Text variant="subtitle2">Usage</Text>
            <Text variant="body2">
              Shows total used resources{' '}
              {perHostMetrics ? (
                <>
                  on <b>{hosts?.[0]?.name}</b>
                </>
              ) : (
                'across all hosts'
              )}
              .
            </Text>
          </div>
        </>
      )}
      <div className={classes.hostCards}>
        <UsageWidget
          icon="microchip"
          title="CPU"
          stats={totals.compute}
          units="GHz"
          primary={`out of ${totals.compute?.max?.toFixed(1)} GHz`}
          metricKey={
            perHostMetrics
              ? METRICS_API_KEYS.PER_HOST_CPU_USAGE
              : showOnlyForSelectedHosts
              ? METRICS_API_KEYS.SELECTED_HOST_CPU_USAGE
              : METRICS_API_KEYS.ALL_HOSTS_CPU_USAGE
          }
          resourceId={hosts[0]?.id}
          hostIds={showOnlyForSelectedHosts ? hostIds : []}
        />
        <UsageWidget
          icon="memory"
          title="Memory"
          stats={totals.memory}
          units="GiB"
          primary={`out of ${totals.memory?.max?.toFixed(1)} GiB`}
          metricKey={
            perHostMetrics
              ? METRICS_API_KEYS.PER_HOST_MEMORY_USAGE
              : showOnlyForSelectedHosts
              ? METRICS_API_KEYS.SELECTED_HOST_MEMORY_USAGE
              : METRICS_API_KEYS.ALL_HOSTS_MEMORY_USAGE
          }
          resourceId={hosts[0]?.id}
          hostIds={showOnlyForSelectedHosts ? hostIds : []}
        />
        <UsageWidget
          icon="sim-card"
          title="Storage"
          stats={totals.disk}
          units="GiB"
          primary={`out of ${totals.disk?.max?.toFixed(1)} GiB`}
          metricKey={
            perHostMetrics
              ? METRICS_API_KEYS.PER_HOST_DISK_USAGE
              : showOnlyForSelectedHosts
              ? METRICS_API_KEYS.SELECTED_HOST_DISK_USAGE
              : METRICS_API_KEYS.ALL_HOSTS_DISK_USAGE
          }
          resourceId={hosts[0]?.id}
          hostIds={showOnlyForSelectedHosts ? hostIds : []}
        />
      </div>
    </div>
  )
}
